/**
 * 认证服务 - 集成钱包连接和后端API
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
  }

  /**
   * 获取签名nonce
   */
  async getNonce(walletAddress) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/nonce`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress.toLowerCase()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('获取nonce失败:', error);
      throw error;
    }
  }

  /**
   * 钱包登录
   */
  async walletLogin(walletAddress, signature, message, deviceInfo = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/wallet/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress.toLowerCase(),
          signature: signature,
          message: message,
          device_info: JSON.stringify({
            device_type: 'web',
            browser: navigator.userAgent,
            ...deviceInfo
          })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // 保存认证信息
      this.token = data.access_token;
      this.refreshToken = data.refresh_token;
      this.user = data.user;
      
      localStorage.setItem('access_token', this.token);
      localStorage.setItem('refresh_token', this.refreshToken);
      localStorage.setItem('user', JSON.stringify(this.user));

      return data;
    } catch (error) {
      console.error('钱包登录失败:', error);
      throw error;
    }
  }

  /**
   * 登出
   */
  async logout() {
    try {
      if (this.token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          }
        });
      }
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 清除本地存储
      this.token = null;
      this.refreshToken = null;
      this.user = null;
      
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  /**
   * 刷新token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      this.token = data.access_token;
      localStorage.setItem('access_token', this.token);
      
      return data;
    } catch (error) {
      console.error('刷新token失败:', error);
      // 刷新失败，清除所有认证信息
      await this.logout();
      throw error;
    }
  }

  /**
   * 获取当前用户信息
   */
  async getCurrentUser() {
    if (!this.token) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token过期，尝试刷新
          await this.refreshAccessToken();
          return this.getCurrentUser();
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      this.user = userData;
      localStorage.setItem('user', JSON.stringify(userData));
      
      return userData;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      throw error;
    }
  }

  /**
   * 带认证的API请求
   */
  async authenticatedRequest(url, options = {}) {
    if (!this.token) {
      throw new Error('No access token available');
    }

    const requestOptions = {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      }
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (response.status === 401) {
        // Token过期，尝试刷新
        await this.refreshAccessToken();
        requestOptions.headers['Authorization'] = `Bearer ${this.token}`;
        return fetch(url, requestOptions);
      }
      
      return response;
    } catch (error) {
      console.error('认证请求失败:', error);
      throw error;
    }
  }

  /**
   * 检查是否已登录
   */
  isLoggedIn() {
    return !!this.token && !!this.user;
  }

  /**
   * 获取当前用户
   */
  getUser() {
    return this.user;
  }

  /**
   * 获取访问token
   */
  getToken() {
    return this.token;
  }
}

// 导出单例实例
export const authService = new AuthService();
export default authService;
